import classNames from "classnames";
import React, { type Ref, useImperativeHandle, useRef } from "react";
import "./Form.css";
import { Lib } from "albertine-shared-web";
import { PictogramIcon } from "../Icon";
import Label from "../Label";

type BaseProps = {
    className?: string;
    children: React.ReactNode;
};

type SpacerProps = {
    className?: string;
    size?: "small" | "medium";
};

type DividerProps = {
    className?: string;
};

type FormProps = {
    className?: string;
    children: React.ReactNode;
} & React.DetailedHTMLProps<React.FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>;

function FormBase(props: FormProps) {
    const { children, className, ...rest } = props;
    return (
        <form className={classNames("form", className)} {...rest}>
            {children}
        </form>
    );
}

type FormInputs = {
    disabled?: boolean;
} & BaseProps;

const FormInputs = React.forwardRef(
    (props: FormInputs, ref: Ref<HTMLDivElement | null>) => {
        const { children, className, disabled } = props;

        const divRef = useRef<HTMLDivElement>(null);
        useImperativeHandle(ref, () => {
            if (!divRef.current) {
                throw new Error(
                    "FormInputs ref is null - component not properly mounted",
                );
            }
            return divRef.current;
        });

        return (
            <div
                className={classNames(
                    "form__inputs",
                    { "form__inputs--disabled": disabled },
                    className,
                )}
                ref={divRef}
            >
                {children}
            </div>
        );
    },
);

type FormErrorProps = {
    errors: string[];
};

function FormErrors(props: FormErrorProps) {
    const { errors } = props;

    return (
        <Lib.Flex.Row gap="value24" alignItems="start" className="form-errors">
            <PictogramIcon.PriorityHigh.Small.E90 />

            <Lib.Flex.Column gap="value8">
                <Label.Medium.Light.N100>Please review</Label.Medium.Light.N100>

                {errors.length !== 0 && (
                    <ul>
                        {errors.map((error, index) => (
                            <li key={`${error}_${index}`}>
                                <Label.Medium.Light.N100>
                                    {error}
                                </Label.Medium.Light.N100>
                            </li>
                        ))}
                    </ul>
                )}
            </Lib.Flex.Column>
        </Lib.Flex.Row>
    );
}

type FormInfoProps = {
    text: string;
};

function FormInfo(props: FormInfoProps) {
    const { text } = props;

    return (
        <Lib.Flex.Column gap="value24" alignItems="start" className="form-info">
            <Label.Medium.Light.N100>{text}</Label.Medium.Light.N100>
        </Lib.Flex.Column>
    );
}

function FormActions(props: BaseProps) {
    const { children, className } = props;
    return <div className={classNames("form__actions", className)}>{children}</div>;
}

function FormSpacer(props: SpacerProps) {
    const { className, size = "medium" } = props;
    return (
        <div
            className={classNames(
                "form__spacer",
                {
                    "form__spacer--small": size === "small",
                    "form__spacer--medium": size === "medium",
                },
                className,
            )}
        />
    );
}

function FormDivider(props: DividerProps) {
    const { className } = props;
    return <div className={classNames("form__divider", className)} />;
}

type StaticValuesProps = {
    className?: string;
    primary: string;
    secondary?: string;
};

function FormStaticValues(props: StaticValuesProps) {
    const { className, primary, secondary } = props;
    return (
        <div className={classNames("form__static-values", className)}>
            <div className="form__static-values-primary">{primary}</div>
            {secondary && (
                <div className="form__static-values-secondary">{secondary}</div>
            )}
        </div>
    );
}

function FormHeading3(props: BaseProps) {
    const { children, className } = props;
    return <h3 className={classNames("form__section-title", className)}>{children}</h3>;
}

export const Form = {
    Base: FormBase,
    Heading3: FormHeading3,
    Inputs: FormInputs,
    Errors: FormErrors,
    Info: FormInfo,
    Actions: FormActions,
    Spacer: FormSpacer,
    Divider: FormDivider,
    StaticValues: FormStaticValues,
};
